import axios from 'axios';
import { getApiBaseUrl } from './apiBase';
const API_BASE_URL = getApiBaseUrl();
const API_URL = `${API_BASE_URL}/shipments`;

export const shipmentService = {
  // Admin/Agent: Get shipment statistics
  getStats: async (token, params = {}) => {
    const qp = new URLSearchParams(params).toString();
    const url = qp ? `${API_URL}/stats?${qp}` : `${API_URL}/stats`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  updateCod: async (id, action, token, payload = {}) => {
    const response = await axios.put(`${API_URL}/${id}/cod`, { action, ...payload }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  getAgentUpdateStats: async (token, window = 'today') => {
    const params = new URLSearchParams();
    if (window) params.set('window', window);
    const response = await axios.get(`${API_URL}/agent/updates/count?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data; // { today, allTime }
  },
  listAllPrivileged: async ({ limit = 20, offset = 0, period = '', status = '', q = '' } = {}, token) => {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    if (period) params.set('period', period);
    if (status) params.set('status', status);
    if (q) params.set('q', q);
    const response = await axios.get(`${API_URL}/all?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  list: async ({ limit = 20, offset = 0, full = false, period = '', status = '', q = '' } = {}, token) => {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    if (full) params.set('full', '1');
    if (period) params.set('period', period);
    if (status) params.set('status', status);
    if (q) params.set('q', q);
    const response = await axios.get(`${API_URL}?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data; // { total_shipments, shipments }
  },

  getById: async (id, token) => {
    const response = await axios.get(`${API_URL}/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  subscribeStream: (token) => {
    const streamUrl = `${API_URL}/stream?token=${encodeURIComponent(token)}`;
    return new EventSource(streamUrl);
  },
  createShipment: async (shipmentData, token) => {
    const response = await axios.post(`${API_URL}`, shipmentData, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 20000,
      validateStatus: (status) => status >= 200 && status < 300
    });
    return response.data;
  },

  createAgentShipment: async (shipmentData, token) => {
    const response = await axios.post(`${API_URL}/agent-book`, shipmentData, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 20000,
      validateStatus: (status) => status >= 200 && status < 300
    });
    return response.data;
  },
  
  getPending: async (token) => {
    const response = await axios.get(`${API_URL}/pending`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Fetch shipments belonging to the authenticated user
  getMy: async (token) => {
    const response = await axios.get(`${API_URL}/my`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  // Update shipment status (agent/admin)
  updateStatus: async (id, status, note = '', token) => {
    const response = await axios.put(`${API_URL}/${id}/status`, { status, note }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data; // { success, shipment }
  },

  // Cancel shipment (Admin only)
  cancelShipment: async (id, note = '', token) => {
    const response = await axios.put(`${API_URL}/${id}/cancel`, { note }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data; // { success, shipment }
  },

  // Update payment status (user/admin)
  updatePaymentStatus: async (id, paymentStatus, token) => {
    const response = await axios.put(`${API_URL}/${id}/payment`, { paymentStatus }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data; // { success, shipment }
  },

  // Update payment status by tracking code (fallback)
  updatePaymentStatusByCode: async (code, paymentStatus, token) => {
    const response = await axios.put(`${API_URL}/track/${encodeURIComponent(code)}/payment`, { paymentStatus }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data; // { success, shipment }
  },

  // Track shipment by public tracking code
  track: async (code) => {
    const response = await axios.get(`${API_URL}/track/${encodeURIComponent(code)}`);
    return response.data;
  },

  setCustomPrice: async (id, customPrice, token) => {
    const response = await axios.put(
      `${API_URL}/${id}/custom-price`,
      { customPrice },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  },

  savePaymentReference: async (id, paymentReferenceCode, token) => {
    const response = await axios.put(
      `${API_URL}/${id}/payment-reference`,
      { paymentReferenceCode },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  },

  setPaymentMethod: async (id, paymentMethod, token) => {
    const response = await axios.put(
      `${API_URL}/${id}/payment-method`,
      { paymentMethod },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  },

  uploadPaymentReceipt: async (id, receipt, token) => {
    const response = await axios.put(
      `${API_URL}/${id}/payment-receipt`,
      { receipt },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  },

  confirmPayment: async (id, method, token) => {
    const response = await axios.put(
      `${API_URL}/${id}/confirm-payment`,
      { method },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  }
};
